import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";

export interface QuantityStepperProps {
  value: number;
  min?: number;
  max?: number;
  /** Beside the control: "pcs", "m". Quantities always carry their unit. */
  unit: string;
  onChange: (value: number) => void;
  /** lg = 48px, for touch. */
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  /** What is being counted, for the labels: "Head gasket". */
  label?: string;
  className?: string;
}

// Heights come from the control scale, not a literal, so the phone override reaches them
// (KALI-44). Same pixels on desktop (32/40/48); 44/48/54 below Tailwind's md.
const HEIGHT = { sm: "h-(--control-sm)", md: "h-(--control-md)", lg: "h-(--control-lg)" } as const;
const BUTTON = { sm: "w-(--control-sm)", md: "w-(--control-md)", lg: "w-(--control-lg)" } as const;

/**
 * Order-line quantity control (design system QuantityStepper). Whole numbers only.
 *
 * The number is a box the customer can type in as well as step with − and + (KALI-25). It keeps a
 * local draft while being edited and commits only on blur or Enter, so a basket total doesn't jump
 * on every keystroke. Focusing selects what's there, so typing replaces it. An empty or
 * non-positive entry reverts to the current value — nothing is ever removed here (the bin does
 * that). Typed values are clamped to [min, max], the same bounds the − and + buttons respect.
 */
export function QuantityStepper({ value, min = 1, max = 999, unit, onChange, size = "md", disabled, label, className }: QuantityStepperProps) {
  const clamp = (next: number) => Math.max(min, Math.min(max, next));
  const set = (next: number) => onChange(clamp(next));

  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  // Keep the box in step with the value whenever it isn't being typed in (e.g. − / + was used).
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const typed = Number.parseInt(draft, 10);
    if (!Number.isFinite(typed) || typed < 1) {
      setDraft(String(value)); // empty or 0 puts the old number back
      return;
    }
    const next = clamp(typed);
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  const step = (delta: number, icon: typeof Plus, text: string, off: boolean) => (
    <button
      type="button"
      aria-label={label ? `${text}, ${label}` : text}
      disabled={disabled || off}
      onClick={() => set(value + delta)}
      className={cx(
        "flex h-full flex-none cursor-pointer items-center justify-center text-ink-700 hover:bg-paper-1 disabled:cursor-not-allowed disabled:opacity-40",
        BUTTON[size],
      )}
    >
      <Icon icon={icon} size={size === "lg" ? 20 : 16} />
    </button>
  );

  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <span className={cx("inline-flex items-center overflow-hidden rounded-sm border border-border-default bg-surface-card", HEIGHT[size])}>
        {step(-1, Minus, "Fewer", value <= min)}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={label ? `Quantity, ${label}` : "Quantity"}
          value={editing ? draft : String(value)}
          disabled={disabled}
          onFocus={(event) => {
            setEditing(true);
            setDraft(String(value));
            event.target.select();
          }}
          onChange={(event) => setDraft(event.target.value.replace(/\D/g, ""))}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          className={cx(
            "h-full w-14 border-x border-border-subtle bg-surface-card text-center type-sku [font-size:var(--text-base)] text-text-strong",
            "focus:outline-none focus-visible:shadow-focus disabled:opacity-40",
          )}
        />
        {step(1, Plus, "More", value >= max)}
      </span>
      <span className="type-small text-text-muted">{unit}</span>
    </span>
  );
}
